import React, { useEffect, useMemo, useState } from 'react';
import { Card, Select, Space, Typography } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import QaqcPipeInspectorCreate from './QaqcPipeInspectorCreate';
import type { QaqcPipeInspector } from '../../../../types';
import { fetchSqlData } from '../../../../services/api';
import { useGetQaqcPipeInspector, useGetPersonnel } from '../../../../hooks/genericHooks';

const QaqcPipeInspectorEdit: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { record?: QaqcPipeInspector } };
  const params = useParams();
  const [record, setRecord] = useState<QaqcPipeInspector | undefined>(state?.record);
  const { data: inspResp } = useGetQaqcPipeInspector();
  const { data: personnelResp } = useGetPersonnel();

  useEffect(() => {
    const load = async () => {
      if (record) return;
      const id = params.id ? Number(params.id) : NaN;
      if (!Number.isFinite(id)) return;
      try {
        const res = await fetchSqlData('qaqc_pipe_inspector', 'id', id);
        const row = Array.isArray(res?.data) ? res.data[0] : (Array.isArray(res) ? res[0] : undefined);
        if (row) setRecord(row as QaqcPipeInspector);
      } catch {}
    };
    load();
  }, [params.id]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/qaqc/piping/tools/inspectors');
  };

  const options = useMemo(() => {
    const rows: QaqcPipeInspector[] = (inspResp?.data as any) || [];
    const persList: any[] = (personnelResp?.data as any) || [];
    return rows.map(w => {
      const p = persList.find(pp => pp.pers_id === w.pers_id);
      const label = p ? `${w.inspector_name} — ${p.first_name} ${p.last_name}` : w.inspector_name;
      return { value: w.id, label, rec: w };
    });
  }, [inspResp, personnelResp]);

  const missingId = !record && !(params.id && Number.isFinite(Number(params.id)));

  return (
    <>
      <style>{`
        /* Remove gray bg and gray text from disabled inputs/selects only in this page */
        .inspector-no-shade .ant-input[disabled],
        .inspector-no-shade .ant-input-affix-wrapper-disabled,
        .inspector-no-shade .ant-select-disabled .ant-select-selector {
          background-color: transparent !important;
          color: inherit !important;
        }
        .inspector-no-shade .ant-select-disabled .ant-select-selection-item,
        .inspector-no-shade .ant-select-disabled .ant-select-arrow {
          color: inherit !important;
        }
      `}</style>
      <div className="inspector-no-shade">
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {missingId && (
            <Card size="small" title={<Typography.Text strong>Select an inspector to edit</Typography.Text>}>
              <Select
                showSearch
                allowClear
                placeholder="Search inspector or personnel"
                style={{ width: 420 }}
                options={options}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label || '').toLowerCase();
                  const tokens = input.trim().toLowerCase().split(/\s+/).filter(Boolean);
                  return tokens.every(t => label.includes(t));
                }}
                onChange={(val, opt: any) => {
                  const id = Number(val);
                  const rec = opt?.rec as QaqcPipeInspector | undefined;
                  if (Number.isFinite(id)) {
                    if (rec) setRecord(rec);
                    navigate(`/qaqc/piping/tools/inspectors/edit/${id}`, { state: { record: rec } });
                  }
                }}
              />
            </Card>
          )}
          <QaqcPipeInspectorCreate
            mode="edit"
            initialValues={record}
            onSaved={() => goBack()}
            onCancel={goBack}
            onBack={goBack}
          />
        </Space>
      </div>
    </>
  );
};

export default QaqcPipeInspectorEdit;
